import { createSelector } from "@reduxjs/toolkit";

import {
  StainsState,
  LabStainsState,
  ExternalLabsState,
  ExternalLabStainsState,
  AntigenRetrievalMethodsState,
  AntibodySpeciesState,
} from "../reducers/frontLoaderReducers";

const selectLabStains = createSelector(
  [
    (_state, labId: number) => labId,
    ({ stains }: { stains: StainsState }) => stains,
    ({ labStains }: { labStains: LabStainsState }) => labStains,
    ({ externalLabs }: { externalLabs: ExternalLabsState }) => externalLabs,
    ({ externalLabStains }: { externalLabStains: ExternalLabStainsState }) =>
      externalLabStains,
    ({
      antigenRetrievalMethods,
    }: {
      antigenRetrievalMethods: AntigenRetrievalMethodsState;
    }) => antigenRetrievalMethods,
    ({ antibodySpecies }: { antibodySpecies: AntibodySpeciesState }) =>
      antibodySpecies,
  ],
  (
    labId,
    stains,
    labStains,
    externalLabs,
    externalLabStains,
    antigenRetrievalMethods,
    antibodySpecies
  ) => {
    if (!labId) return [];

    const filteredLabStains = Object.values(labStains).filter((ls) => {
      // Note that filtering out `is_archived` might want to be an option
      // Right now, this selector is only used when ordering, so it doesn't much matter
      // If you were to use this selector to map a lab stain from an order, it might
      // ... break if you are looking for a lab stain whose underling stain has been
      // ... archived.
      if (ls.is_archived) return false;
      const stain = ls.stain_id ? stains[ls.stain_id] : undefined;
      if (stain?.is_archived) return false;
      return ls.lab_id === labId;
    });

    return filteredLabStains.map((ls) => ({
      ...ls,
      stain: stains[ls.stain_id],
      external_stain: !ls.external_lab_stain_id
        ? undefined
        : {
            ...externalLabStains[ls.external_lab_stain_id],
            stain: stains[externalLabStains[ls.external_lab_stain_id].stain_id],
            external_lab:
              externalLabs[
                externalLabStains[ls.external_lab_stain_id].external_lab_id
              ],
          },
      antigen_retrieval_method: ls.antigen_retrieval_method_id
        ? antigenRetrievalMethods[ls.antigen_retrieval_method_id]
        : undefined,
      antibody_species: ls.antibody_species_id
        ? antibodySpecies[ls.antibody_species_id]
        : undefined,
      routine_stain: ls.routine_stain_id
        ? stains[ls.routine_stain_id]
        : undefined,
      non_routine_stain: ls.non_routine_stain_id
        ? stains[ls.non_routine_stain_id]
        : undefined,
    }));
  }
);

export type MemoizedLabStain = ReturnType<typeof selectLabStains>[0];

export default selectLabStains;
